// src/components/SubmissionList.js
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, deleteDoc, doc, orderBy, limit, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Card, Image, Message, Loader, Button, Modal, Form, Pagination } from 'semantic-ui-react';
import AnimatedIcon from './AnimatedIcon';

export default function SubmissionList() {
  const { currentUser } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [newPrompt, setNewPrompt] = useState('');
  const [showModal, setShowModal] = useState(false);
  const submissionsPerPage = 5; // Show 5 submissions per page

  useEffect(() => {
    const q = query(
      collection(db, 'submissions'),
      where('uid', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(submissionsPerPage * page)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() });
      });
      setSubmissions(items);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser.uid, page]);

  const handleEditClick = (submissionId, currentPrompt) => {
    setEditingId(submissionId);
    setNewPrompt(currentPrompt);
    setShowModal(true);
  };

  const handleUpdatePrompt = async () => {
    if (newPrompt.trim() === '') return;

    try {
      const submissionRef = doc(db, 'submissions', editingId);
      await updateDoc(submissionRef, { prompt: newPrompt });
      setShowModal(false);
      setEditingId(null);
      setNewPrompt('');
    } catch (error) {
      console.error("Error updating prompt:", error);
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    try {
      const submissionRef = doc(db, 'submissions', submissionId);
      await deleteDoc(submissionRef);
    } catch (error) {
      console.error("Error deleting submission:", error);
    }
  };

  const handlePaginationChange = (e, { activePage }) => {
    setPage(activePage);
  };

  if (loading) return <Loader active />;

  if (submissions.length === 0) return <Message info>No submissions yet.</Message>;

  return (
    <>
      <Card.Group itemsPerRow={3}>
        {submissions.map((submission) => (
          <Card key={submission.id}>
            <Card.Content>
              <Card.Header>Original Icon</Card.Header>
              <Image src={submission.iconURL} size="small" />
            </Card.Content>
            <Card.Content>
              <Card.Header>Animated Icon</Card.Header>
              <AnimatedIcon submissionId={submission.id} />
            </Card.Content>
            <Card.Content extra>
              <Button negative onClick={() => handleDeleteSubmission(submission.id)}>
                Delete
              </Button>
            </Card.Content>
          </Card>
        ))}
      </Card.Group>

      {/* Pagination */}
      <Pagination
        activePage={page}
        totalPages={Math.ceil(submissions.length / submissionsPerPage)}
        onPageChange={handlePaginationChange}
        style={{ marginTop: '20px', textAlign: 'center' }}
      />

      {/* Modal for Editing Prompt */}
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Modal.Header>Edit Submission Prompt</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>New Animation Prompt</label>
              <input
                type="text"
                value={newPrompt}
                onChange={(e) => setNewPrompt(e.target.value)}
                placeholder="Enter new prompt..."
              />
            </Form.Field>
            <Form.Button primary onClick={handleUpdatePrompt}>
              Update Prompt
            </Form.Button>
            <Form.Button onClick={() => setShowModal(false)}>Cancel</Form.Button>
          </Form>
        </Modal.Content>
      </Modal>
    </>
  );
}
